import { useEffect, useRef } from 'react';

/**
 * One command context at a time for Ctrl/Cmd+K (Astra PTUX-254).
 *
 * Before this, every palette installed its own window keydown listener, so one keypress opened the PNR record palette and the
 * global search together. Each palette now registers here instead:
 * - `record` scope: a palette tied to the page being worked on (e.g. the PNR actions palette). It wins Ctrl+K while mounted.
 * - `global` scope: the app-wide search. It opens only when no record palette is mounted, or through "Search everywhere".
 * One capture-phase listener decides; it stops the event so no second panel can react to the same press.
 * Escape closes only the most recently opened surface and gives focus back to where it was opened from.
 */
export type CommandScope = 'record' | 'global';

interface Entry {
  id: string;
  scope: CommandScope;
  isOpen: () => boolean;
  setOpen: (open: boolean) => void;
}

const entries = new Map<string, Entry>();
/** Surfaces that are open now, oldest first. Escape closes the last one. */
const openStack: string[] = [];
const openers = new Map<string, HTMLElement | null>();
let listening = false;

const SCOPE_PRIORITY: Record<CommandScope, number> = { record: 2, global: 1 };

/** Typing surfaces where Ctrl+K should stay with the field. A search box (type=search) is the exception: it is a launch point. */
function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  if (target instanceof HTMLElement && target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') {
    const type = (target.getAttribute('type') || 'text').toLowerCase();
    return !['search', 'button', 'checkbox', 'radio', 'submit', 'reset'].includes(type) && !target.hasAttribute('data-command-launcher');
  }
  return false;
}

/** A modal that is not one of ours owns the keyboard. */
function isInsideForeignModal(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  const modal = target.closest('[role="dialog"][aria-modal="true"], [role="dialog"][aria-modal=""], dialog[open]');
  return !!modal && !modal.hasAttribute('data-command-surface');
}

function topOpenEntry(): Entry | undefined {
  for (let i = openStack.length - 1; i >= 0; i -= 1) {
    const entry = entries.get(openStack[i]);
    if (entry && entry.isOpen()) return entry;
  }
  return undefined;
}

function pickEntryToOpen(): Entry | undefined {
  let best: Entry | undefined;
  entries.forEach((entry) => {
    if (!best || SCOPE_PRIORITY[entry.scope] > SCOPE_PRIORITY[best.scope]) best = entry;
  });
  return best;
}

function claim(event: KeyboardEvent): void {
  event.preventDefault();
  event.stopImmediatePropagation();
}

function onKeyDown(event: KeyboardEvent): void {
  const key = (event.key || '').toLowerCase();
  if (key === 'escape') {
    const active = topOpenEntry();
    if (!active) return;
    claim(event);
    active.setOpen(false);
    return;
  }
  if (key !== 'k' || !(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) return;
  const active = topOpenEntry();
  if (active) {
    claim(event);
    active.setOpen(false);
    return;
  }
  if (isEditableTarget(event.target) || isInsideForeignModal(event.target)) return;
  const next = pickEntryToOpen();
  if (!next) return;
  claim(event);
  next.setOpen(true);
}

function ensureListening(): void {
  if (listening || typeof window === 'undefined') return;
  window.addEventListener('keydown', onKeyDown, true);
  listening = true;
}

function stopListeningIfIdle(): void {
  if (!listening || entries.size > 0 || typeof window === 'undefined') return;
  window.removeEventListener('keydown', onKeyDown, true);
  listening = false;
}

/** Open the surface of a scope on purpose (e.g. the "Search everywhere" switch in a record palette). Closes whatever is open first. */
export function openCommandContext(scope: CommandScope): boolean {
  const target = Array.from(entries.values()).find((entry) => entry.scope === scope);
  if (!target) return false;
  entries.forEach((entry) => { if (entry !== target && entry.isOpen()) entry.setOpen(false); });
  target.setOpen(true);
  return true;
}

export interface UseCommandContextOptions {
  /** Unique per mounted surface. */
  id: string;
  scope: CommandScope;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Set false to leave the registry (e.g. the surface is not available for this user). */
  enabled?: boolean;
}

/** Register a palette as a command context. The caller keeps owning its open state; the registry only asks it to open or close. */
export function useCommandContext({ id, scope, open, onOpenChange, enabled = true }: UseCommandContextOptions): void {
  const openRef = useRef(open);
  openRef.current = open;
  const changeRef = useRef(onOpenChange);
  changeRef.current = onOpenChange;

  useEffect(() => {
    if (!enabled) return undefined;
    entries.set(id, { id, scope, isOpen: () => openRef.current, setOpen: (next) => changeRef.current(next) });
    ensureListening();
    return () => {
      entries.delete(id);
      const at = openStack.indexOf(id);
      if (at >= 0) openStack.splice(at, 1);
      openers.delete(id);
      stopListeningIfIdle();
    };
  }, [id, scope, enabled]);

  // Track opening order and hand focus back to the opener when the surface closes.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (!enabled) return;
    if (open && !wasOpen.current) {
      wasOpen.current = true;
      const at = openStack.indexOf(id);
      if (at >= 0) openStack.splice(at, 1);
      openStack.push(id);
      openers.set(id, typeof document !== 'undefined' && document.activeElement instanceof HTMLElement ? document.activeElement : null);
    } else if (!open && wasOpen.current) {
      wasOpen.current = false;
      const at = openStack.indexOf(id);
      if (at >= 0) openStack.splice(at, 1);
      const opener = openers.get(id);
      openers.delete(id);
      if (opener && opener.isConnected && typeof document !== 'undefined' && (document.activeElement === document.body || !document.activeElement)) {
        opener.focus();
      }
    }
  }, [open, id, enabled]);
}

/** Test seam: forget every registration. */
export function resetCommandContextsForTests(): void {
  entries.clear();
  openStack.length = 0;
  openers.clear();
  if (listening && typeof window !== 'undefined') {
    window.removeEventListener('keydown', onKeyDown, true);
    listening = false;
  }
}
